import { useCallback } from "react";
import { useGameStateContext } from "../GameStateProvider";
import { useAudioContext } from "../AudioProvider";
import { CONSTANTS } from "../data";

export const useShopActions = () => {
  const { state, dispatch } = useGameStateContext();
  const audio = useAudioContext();

  const handlePurchaseItem = useCallback(
    (itemId: string, quantity: number) => {
      const shopItem = CONSTANTS.SHOP_ITEMS[itemId];
      if (!shopItem) {
        throw new Error(`Shop item not found: ${itemId}`);
      }

      const totalCost = shopItem.price * quantity;
      if (state.coins < totalCost) return;

      if (itemId === "brew_station_2") {
        if (state.brewStations.length < 2) {
          dispatch({ type: "SPEND_COINS", amount: totalCost });
          dispatch({ type: "ADD_BREW_STATION" });
          audio.playSfx("cash-register");
        }
      } else {
        dispatch({
          type: "PURCHASE_ITEM",
          itemId,
          quantity,
          totalCost,
        });
        audio.playSfx("cash-register");
      }
    },
    [state.coins, state.brewStations.length, dispatch, audio]
  );

  const handlePurchaseRecipe = useCallback(
    (recipeId: string) => {
      const recipeShopItem = CONSTANTS.RECIPE_SHOP_ITEMS[recipeId];
      if (!recipeShopItem) {
        throw new Error(`Recipe shop item not found: ${recipeId}`);
      }

      const totalCost = recipeShopItem.price;
      if (state.coins < totalCost) return;

      // Check if recipe is already discovered
      if (state.discoveredRecipes.includes(recipeId)) return;

      // Purchase the recipe
      dispatch({ type: "SPEND_COINS", amount: totalCost });
      dispatch({ type: "DISCOVER_RECIPE", recipeId });
      audio.playSfx("discovery");
    },
    [state.coins, state.discoveredRecipes, dispatch, audio]
  );

  const handleUpgradeAiBarista = useCallback(() => {
    const currentLevel = state.aiBarista.level;
    const isUnlocking = currentLevel === 0;
    const unlockCost = CONSTANTS.AI_BARISTA.UNLOCK_COST;
    const unlockLevelReq = CONSTANTS.AI_BARISTA.UNLOCK_LEVEL;
    const nextLevelData = CONSTANTS.AI_BARISTA.LEVELS[currentLevel + 1];

    if (isUnlocking) {
      if (state.level >= unlockLevelReq && state.coins >= unlockCost) {
        dispatch({ type: "UPGRADE_AI_BARISTA" });
        audio.playSfx("cash-register");
      }
    } else if (nextLevelData && state.coins >= nextLevelData.cost) {
      dispatch({ type: "UPGRADE_AI_BARISTA" });
      audio.playSfx("cash-register");
    }
  }, [state, dispatch, audio]);

  const handleUpgradeEquipment = useCallback(
    (equipmentId: "grinder" | "espresso_machine") => {
      const currentLevel = state.equipmentLevels[equipmentId];
      const upgradeInfo = (CONSTANTS.EQUIPMENT_UPGRADES as any)[equipmentId]?.levels[currentLevel + 1];

      if (upgradeInfo && state.coins >= upgradeInfo.cost && state.level >= upgradeInfo.unlockLevel) {
        dispatch({ type: "UPGRADE_EQUIPMENT", equipmentId });
        audio.playSfx("cash-register");
      }
    },
    [state, dispatch, audio]
  );

  const handlePurchaseDecoration = useCallback(
    (decorationId: string) => {
      const decoMap = (CONSTANTS as any).DECORATION_SHOP_ITEMS || {};
      const deco = decoMap[decorationId];
      if (!deco) {
        throw new Error(`Decoration not found: ${decorationId}`);
      }
      if ((state as any).decorations?.[decorationId]) {
        // Already purchased
        return;
      }
      if (deco.unlockLevel && state.level < deco.unlockLevel) {
        return;
      }
      if (state.coins < deco.price) {
        return;
      }

      dispatch({ type: "PURCHASE_DECORATION", decorationId });
      audio.playSfx("cash-register");
    },
    [state, dispatch, audio]
  );

  return {
    handlePurchaseItem,
    handlePurchaseRecipe,
    handleUpgradeAiBarista,
    handleUpgradeEquipment,
    handlePurchaseDecoration,
  };
};
